<script setup lang="ts">
/**
 * Участники проекта — макет 84:233: поиск сотрудника с ролью и кнопкой, список
 * строк 44 и подпись про роли.
 *
 * Справочник сотрудников живёт в auth и отдаётся страницами без поиска, поэтому
 * его тянут разом и ищут на месте. Права `users:read` есть не у всех, и отказ
 * здесь не молчит: список кандидатов подписывает, почему он пуст — искать
 * причину в другом месте человеку неоткуда.
 */
import { AppAvatar, AppButton, AppSelect, AppTextField } from '@vinprop/design-system'
import { UserRound } from 'lucide-vue-next'
import { computed, onMounted, ref } from 'vue'
import { useI18n } from 'vue-i18n'

import InlinePicker from '@/components/InlinePicker.vue'
import { authApi } from '@/features/auth/api'
import type { Person } from '@/features/auth/types'

import { MEMBER_ROLES } from '../api'
import { useSettingsStore } from '../store'

import type { V1MemberRole } from '@/api/clients/tracker-project'

/** Сколько кандидатов показываем под полем: длиннее список не читают */
const SUGGESTIONS = 6

const { t } = useI18n()
const store = useSettingsStore()

const query = ref('')
const role = ref<V1MemberRole>('MEMBER_ROLE_MEMBER')
const people = ref<Person[]>([])
const isDirectoryOpen = ref(true)
const picked = ref<Person | null>(null)

const roleOptions = computed(() =>
  MEMBER_ROLES.map((value) => ({ value, label: t(`settings.members.roles.${value}`) })),
)

onMounted(async () => {
  try {
    people.value = await authApi.listUsers()
  } catch {
    // Нет права на справочник — поле остаётся, но кандидатов в нём не будет.
    isDirectoryOpen.value = false
  }
})

const inProject = computed(() => new Set(store.members.map((member) => member.userId)))

const candidates = computed(() => {
  const needle = query.value.trim().toLowerCase()

  if (!needle) return []

  return people.value
    .filter(
      (person) =>
        !inProject.value.has(person.id) &&
        (person.name.toLowerCase().includes(needle) ||
          person.email.toLowerCase().includes(needle)),
    )
    .slice(0, SUGGESTIONS)
})

const pick = (person: Person) => {
  picked.value = person
  query.value = person.name || person.email
}

const add = () => {
  const person = picked.value ?? candidates.value[0]

  if (!person) return

  store.addMember({ userId: person.id, name: person.name, email: person.email, role: role.value })
  query.value = ''
  picked.value = null
}
</script>

<template>
  <div class="mtab">
    <div class="mtab__add">
      <div class="mtab__search">
        <AppTextField
          v-model="query"
          :placeholder="t('settings.members.searchPlaceholder')"
          :prefix-icon="UserRound"
          :aria-label="t('settings.members.searchPlaceholder')"
          @update:model-value="picked = null"
        />

        <div v-if="query.trim()" class="mtab__suggest">
          <p v-if="!isDirectoryOpen" class="mtab__suggest-empty">
            {{ t('settings.members.noDirectory') }}
          </p>
          <p v-else-if="!candidates.length" class="mtab__suggest-empty">
            {{ t('settings.members.nobody') }}
          </p>
          <button
            v-for="person in candidates"
            :key="person.id"
            type="button"
            class="mtab__suggest-item"
            @click="pick(person)"
          >
            <AppAvatar :name="person.name" :size="20" />
            <span class="mtab__suggest-name">{{ person.name || person.email }}</span>
            <span class="mtab__suggest-mail">{{ person.email }}</span>
          </button>
        </div>
      </div>

      <AppSelect
        v-model="role"
        class="mtab__role"
        :options="roleOptions"
        :aria-label="t('settings.members.role')"
        size="small"
      />

      <AppButton variant="primary" :disabled="!picked && !candidates.length" @click="add">
        {{ t('settings.members.add') }}
      </AppButton>
    </div>

    <p class="mtab__overline">
      {{ t('settings.members.title') }} · {{ store.members.length }}
    </p>

    <div class="mtab__card">
      <div v-for="member in store.members" :key="member.userId" class="mrow">
        <AppAvatar :name="member.name" :size="24" />
        <span class="mrow__name">{{ member.name || member.userId }}</span>
        <span class="mrow__mail">{{ member.email }}</span>
        <span class="mrow__spacer" />

        <InlinePicker
          align="right"
          :options="roleOptions"
          :model-value="member.role"
          :empty-text="t('toolbar.filters.empty')"
          @update:model-value="store.setRole(member.userId, $event as V1MemberRole)"
        >
          <template #trigger="{ toggle }">
            <button type="button" class="mrow__role" @click="toggle()">
              {{ t(`settings.members.roles.${member.role || 'MEMBER_ROLE_MEMBER'}`) }} ▾
            </button>
          </template>
        </InlinePicker>
      </div>
    </div>

    <p class="mtab__hint">{{ t('settings.members.hint') }}</p>
  </div>
</template>

<style scoped>
.mtab {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.mtab__add {
  display: flex;
  align-items: center;
  gap: 12px;
}

.mtab__search {
  position: relative;
  width: 416px;
}

.mtab__role {
  width: 156px;
}

/* Кандидаты падают поверх списка участников, а не раздвигают его: иначе строки
   под полем прыгали бы на каждую набранную букву. */
.mtab__suggest {
  position: absolute;
  top: 44px;
  left: 0;
  z-index: 20;
  display: flex;
  flex-direction: column;
  width: 100%;
  padding: 4px;
  border: 1px solid var(--color-border);
  border-radius: var(--radius-md);
  background: var(--color-surface);
  box-shadow: var(--shadow-md);
}

.mtab__suggest-empty {
  margin: 0;
  padding: 8px 10px;
  color: var(--color-text-muted);
  font-size: var(--font-size-body-sm);
}

.mtab__suggest-item {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 8px;
  border: none;
  border-radius: var(--radius-sm);
  background: none;
  font-family: inherit;
  text-align: left;
  cursor: pointer;
}

.mtab__suggest-item:hover {
  background: var(--color-bg-muted);
}

.mtab__suggest-name {
  color: var(--color-text-primary);
  font-size: var(--font-size-body-sm);
}

.mtab__suggest-mail {
  overflow: hidden;
  color: var(--color-text-muted);
  font-size: var(--font-size-caption);
  text-overflow: ellipsis;
  white-space: nowrap;
}

.mtab__overline {
  margin: -8px 0 -12px;
  color: var(--color-text-muted);
  font-family: var(--font-family-mono);
  font-size: var(--font-size-overline);
  letter-spacing: 0.04em;
  text-transform: uppercase;
}

.mtab__card {
  display: flex;
  flex-direction: column;
  border: 1px solid var(--color-border);
  border-radius: var(--radius-xl);
  background: var(--color-surface);
  overflow: hidden;
}

.mrow {
  display: flex;
  align-items: center;
  gap: 12px;
  height: 44px;
  padding: 0 16px;
  border-top: 1px solid var(--color-border);
}

.mrow:first-child {
  border-top: none;
}

.mrow__name {
  color: var(--color-text-primary);
  font-size: var(--font-size-body);
  font-weight: var(--font-weight-medium);
}

.mrow__mail {
  color: var(--color-text-muted);
  font-size: var(--font-size-body-sm);
}

.mrow__spacer {
  flex: 1;
}

.mrow__role {
  padding: 4px 10px;
  border: none;
  border-radius: 7px;
  background: var(--color-bg-muted);
  color: var(--color-text-secondary);
  font-family: inherit;
  font-size: var(--font-size-caption);
  font-weight: var(--font-weight-medium);
  white-space: nowrap;
  cursor: pointer;
}

.mtab__hint {
  margin: 0;
  color: var(--color-text-muted);
  font-size: var(--font-size-body-sm);
}
</style>
